import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LocationType, LocationTypeField, SbcApiService } from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import { StatusBadgeComponent } from '../components/status-badge.component';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { WorkspaceIconsModule } from '../components/workspace-icons.module';

@Component({
  selector: 'app-location-types',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, StatusBadgeComponent, WorkspaceIconsModule, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './location-types.component.html',
  styleUrl: './location-types.component.css',
})
export class LocationTypesComponent {
  private readonly api = inject(SbcApiService);
  readonly session = inject(SessionService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly toast = signal('');
  readonly rows = signal<LocationType[]>([]);
  readonly search = signal('');
  readonly typeModalOpen = signal(false);
  readonly editingTypeId = signal<number | null>(null);

  typeForm = {
    code: '',
    name: '',
    fields: [] as LocationTypeField[],
  };

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => this.reload());
    });
  }

  filteredRows(): LocationType[] {
    const q = this.search().trim().toLowerCase();
    const rows = this.rows();
    if (!q) return rows;
    return rows.filter(
      (t) =>
        t.code.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        t.fields.some((f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q))
    );
  }

  activeCount(): number {
    return this.rows().filter((t) => t.statusCd === 'active').length;
  }

  totalFields(): number {
    return this.rows().reduce((n, t) => n + (t.fields?.length || 0), 0);
  }

  reload(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listLocationTypes({ includeInactive: 'true' }).subscribe({
      next: (res) => {
        this.rows.set(res.data || []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load location types');
      },
    });
  }

  openCreateType(): void {
    this.editingTypeId.set(null);
    this.typeForm = {
      code: '',
      name: '',
      fields: [{ key: '', label: '', field_type: 'text', is_required: false }],
    };
    this.typeModalOpen.set(true);
  }

  openEditType(type: LocationType): void {
    this.editingTypeId.set(type.id);
    this.typeForm = {
      code: type.code,
      name: type.name,
      fields: type.fields?.length
        ? type.fields.map((f) => ({ ...f }))
        : [{ key: '', label: '', field_type: 'text', is_required: false }],
    };
    this.typeModalOpen.set(true);
  }

  closeTypeModal(): void {
    this.typeModalOpen.set(false);
  }

  addTypeFieldRow(): void {
    this.typeForm.fields.push({ key: '', label: '', field_type: 'text', is_required: false });
  }

  removeTypeFieldRow(index: number): void {
    this.typeForm.fields.splice(index, 1);
  }

  saveType(): void {
    const fields = this.typeForm.fields.filter((f) => f.key.trim() && f.label.trim());
    if (!this.typeForm.code.trim() || !this.typeForm.name.trim() || !fields.length) {
      this.showToast('Code, name, and at least one field are required');
      return;
    }

    this.saving.set(true);
    const body = {
      code: this.typeForm.code.trim(),
      name: this.typeForm.name.trim(),
      fields: fields.map((f) => ({
        key: f.key.trim(),
        label: f.label.trim(),
        field_type: f.field_type || 'text',
        is_required: !!f.is_required,
      })),
    };

    const editId = this.editingTypeId();
    const req = editId
      ? this.api.updateLocationType(editId, { name: body.name, fields: body.fields })
      : this.api.createLocationType(body);

    req.subscribe({
      next: () => {
        this.saving.set(false);
        this.typeModalOpen.set(false);
        this.showToast(editId ? 'Location type updated' : 'Location type created');
        this.reload();
      },
      error: (err) => {
        this.saving.set(false);
        this.showToast(err?.error?.message || 'Save failed');
      },
    });
  }

  deactivateType(type: LocationType): void {
    if (!confirm(`Deactivate type "${type.name}"?`)) return;
    this.api.deleteLocationType(type.id).subscribe({
      next: () => {
        this.showToast(`${type.name} deactivated`);
        this.reload();
      },
      error: (err) => this.showToast(err?.error?.message || 'Deactivate failed'),
    });
  }

  private showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }
}
